import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Lobos — dodatki na karcie torebki (zamiast Selleasy, 5.10.2026). Markup: blocks/lobos-dodatki.liquid.
 *
 * Nic nie zaznaczone: nie ingerujemy — dodaje zwykły formularz motywu (assets/product-form.js).
 * Zaznaczone inicjały lub przypinka: przejmujemy wysyłkę formularza produktu z tej samej sekcji
 * (submit w fazie capture na window, przed delegacją motywu na document) i wysyłamy jedno żądanie
 * /cart/add.js: torebka + dodatki jako pozycje zagnieżdżone (parent_id = wariant torebki), jak na kartach LxR III.
 * Gdy zagnieżdżenie się nie uda, druga próba bez parent_id (właściwość „Torebka” wiąże inicjały z torebką).
 * Potem CartLinesUpdateEvent (szuflada odświeża się i otwiera, licznik koszyka, przycisk w pasku na dole).
 * Animacja „lot do koszyka” zostaje z motywu (klik w przycisk). Przy błędzie walidacji klik jest zatrzymany.
 *
 * Przypinki (ETAP 6): po wczytaniu strony sprawdzamy dostępność każdej pokazanej przypinki (/products/<uchwyt>.js),
 * bo HTML strony mógł być z pamięci podręcznej; niedostępne znikają, a bez przypinek znika cały rząd.
 * Gdy dodanie do koszyka się nie uda, a zaznaczona była przypinka: wyprzedane przypinki usuwamy z rzędu
 * i dodajemy resztę (torebka, inicjały, pozostałe przypinki) z krótkim komunikatem.
 */

const MAX_LETTERS = 3;
const MAX_DOTS = 3;
const ONCE = 'QVXYŁŻ';
const LETTER = /^[A-ZŁŻ]$/;
const SOURCE = 'lobos-dodatki';

/** Przechwytywanie klików i wysyłki formularza instalujemy raz na stronę. */
let interceptorsInstalled = false;

/**
 * Inicjały wg matrycy (jak na kartach LxR III): litery A–Z oraz Ł i Ż, do trzech liter, po każdej opcjonalnie kropka;
 * ta sama litera najwyżej dwa razy, Q, V, X, Y, Ł, Ż raz; wielkie litery.
 * @param {string} raw
 * @returns {{ value: string, letters: number, problem: '' | 'bad' | 'twice' | 'once' }}
 */
export function cleanInitials(raw) {
  const text = String(raw || '').toUpperCase();
  /** @type {Record<string, number>} */
  const used = {};
  /** @type {'' | 'bad' | 'twice' | 'once'} */
  let problem = '';
  let value = '';
  let letters = 0;
  let dots = 0;

  for (const char of text) {
    if (char === '.') {
      if (value && !value.endsWith('.') && dots < MAX_DOTS) {
        value += '.';
        dots += 1;
      }
      continue;
    }
    if (!LETTER.test(char)) {
      if (/\S/.test(char)) problem = 'bad';
      continue;
    }
    if (letters >= MAX_LETTERS) continue;
    const limit = ONCE.includes(char) ? 1 : 2;
    if ((used[char] || 0) >= limit) {
      problem = limit === 1 ? 'once' : 'twice';
      continue;
    }
    used[char] = (used[char] || 0) + 1;
    letters += 1;
    value += char;
  }

  return { value, letters, problem };
}

/**
 * Dostępność przypinki teraz (HTML strony mógł być z pamięci podręcznej).
 * @param {string} handle
 * @param {number} variantId
 * @returns {Promise<boolean | null>} null = nie wiadomo (błąd sieci, brak produktu pod tym adresem) — przypinka zostaje
 */
async function flapAvailable(handle, variantId) {
  if (!handle) return null;
  const root = window.Shopify?.routes?.root || '/';
  try {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-cache',
    });
    // 404 i inne błędy: nie wiadomo (np. inny uchwyt w tym języku) — przypinka zostaje,
    // a gdyby nie dało się jej dodać, dodanie i tak przejdzie bez niej
    if (!response.ok) return null;
    const data = await response.json();
    const variants = Array.isArray(data?.variants) ? data.variants : [];
    const variant = variants.find((/** @type {any} */ item) => Number(item.id) === variantId);
    if (variant) return Boolean(variant.available);
    return false;
  } catch {
    return null;
  }
}

/**
 * @param {string} template - tekst z {name}
 * @param {string[]} names
 */
function fillNames(template, names) {
  return template.replace('{name}', names.filter(Boolean).join(', '));
}

/**
 * @typedef {object} LobosDodatkiRefs
 * @property {HTMLInputElement} [iniToggle]
 * @property {HTMLElement} [iniPanel]
 * @property {HTMLInputElement} [iniInput]
 * @property {HTMLElement} [iniCount]
 * @property {HTMLElement} [iniMsg]
 * @property {HTMLInputElement[]} [flapInputs]
 * @property {HTMLElement} [err]
 *
 * @extends Component<LobosDodatkiRefs>
 */
export class LobosDodatki extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Promise<void> | null} */
  #pendingVariant = null;

  /** @type {boolean} */
  busy = false;

  connectedCallback() {
    super.connectedCallback();
    installInterceptors();

    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#abortController.signal,
    });

    this.#syncPanel();
    this.#scheduleFlapCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** Zmiana wariantu w toku: przed dodaniem czekamy, aż motyw ustawi nowy wariant w formularzu. */
  #onProductSelect = (/** @type {Event & { promise?: Promise<unknown> }} */ event) => {
    if (!event.promise) return;
    const settled = event.promise.then(
      () => undefined,
      () => undefined
    );
    this.#pendingVariant = settled;
    settled.then(() => {
      if (this.#pendingVariant === settled) this.#pendingVariant = null;
    });
  };

  get initialsOn() {
    return Boolean(this.refs.iniToggle?.checked);
  }

  get initials() {
    return cleanInitials(this.refs.iniInput?.value || '').value;
  }

  /** @returns {number[]} */
  get selectedFlaps() {
    return (this.refs.flapInputs || [])
      .filter((input) => input.checked && !input.disabled)
      .map((input) => Number(input.value))
      .filter(Boolean);
  }

  get hasSelection() {
    return this.initialsOn || this.selectedFlaps.length > 0;
  }

  /** @returns {{ ok: boolean, message?: string, focus?: HTMLElement }} */
  validate() {
    if (this.initialsOn && !this.initials) {
      return { ok: false, message: this.dataset.msgEmpty || '', focus: this.refs.iniInput };
    }
    return { ok: true };
  }

  /** Sprawdzenie przypinek po wczytaniu strony, gdy przeglądarka ma wolną chwilę. */
  #scheduleFlapCheck() {
    if (!this.querySelector('.lobos-dodatki__flap[data-flap-handle]')) return;
    const run = () => {
      this.recheckFlaps().catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 1200);
  }

  /**
   * Sprawdza dostępność przypinek w rzędzie i usuwa niedostępne.
   * @returns {Promise<Map<number, string>>} usunięte przypinki: wariant -> nazwa
   */
  async recheckFlaps() {
    const cards = [...this.querySelectorAll('.lobos-dodatki__flap[data-flap-handle]')];
    /** @type {Map<number, string>} */
    const gone = new Map();
    await Promise.all(
      cards.map(async (card) => {
        if (!(card instanceof HTMLElement)) return;
        const input = card.querySelector('input');
        const id = Number(input?.value);
        if (!id) return;
        const available = await flapAvailable(card.dataset.flapHandle || '', id);
        if (available === false) {
          gone.set(id, card.querySelector('.lobos-dodatki__flap-name')?.textContent?.trim() || '');
        }
      })
    );
    if (gone.size) this.#removeFlaps(gone);
    return gone;
  }

  /** @param {Map<number, string>} gone */
  #removeFlaps(gone) {
    for (const card of this.querySelectorAll('.lobos-dodatki__flap')) {
      const input = card.querySelector('input');
      if (!(input instanceof HTMLInputElement) || !gone.has(Number(input.value))) continue;
      input.checked = false;
      input.disabled = true;
      card.remove();
    }
    if (this.querySelector('.lobos-dodatki__flap')) return;
    const fieldset = this.querySelector('.lobos-dodatki__flaps-fs');
    if (fieldset instanceof HTMLElement) fieldset.hidden = true;
    if (!this.querySelector('.lobos-dodatki__ini')) this.hidden = true;
  }

  /** @param {number[]} ids */
  #uncheckFlaps(ids) {
    for (const input of this.refs.flapInputs || []) {
      if (ids.includes(Number(input.value))) input.checked = false;
    }
  }

  /** Obsługa przełącznika inicjałów (on:change). */
  handleIniToggle() {
    this.#syncPanel();
    this.#setError('');
    if (this.initialsOn) this.refs.iniInput?.focus({ preventScroll: true });
  }

  /** Czyszczenie wpisu do zasad matrycy (on:input). */
  handleIniInput() {
    const input = this.refs.iniInput;
    if (!input) return;
    const result = cleanInitials(input.value);
    if (input.value !== result.value) input.value = result.value;
    this.#updateCount(result.letters);
    const key = result.problem ? `msg${result.problem.charAt(0).toUpperCase()}${result.problem.slice(1)}` : '';
    this.#setMessage(key ? this.dataset[key] || '' : '');
    this.#setError('');
  }

  /** Zmiana zaznaczenia przypinki (on:change). */
  handleFlapChange() {
    this.#setError('');
  }

  /**
   * Pokazuje błąd walidacji przy przycisku dodawania.
   * @param {{ message?: string, focus?: HTMLElement }} check
   * @param {boolean} [scroll]
   */
  showInvalid(check, scroll = false) {
    this.#setError(check.message || '');
    if (scroll) this.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    check.focus?.focus({ preventScroll: scroll });
  }

  /**
   * Dodaje torebkę z dodatkami jednym żądaniem.
   * @param {HTMLFormElement} form - formularz produktu motywu (buy-buttons)
   */
  async submit(form) {
    if (this.busy) return;
    const check = this.validate();
    if (!check.ok) {
      this.showInvalid(check);
      return;
    }

    this.busy = true;
    this.#setError('');
    const productForm = form.closest('product-form-component');
    const source = productForm instanceof HTMLElement ? productForm : this;

    try {
      if (this.#pendingVariant) {
        await this.#pendingVariant;
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));
      if (!variantId) throw new Error('');
      const quantity =
        Number(formData.get('quantity')) || Number(source.dataset.quantityDefault) || 1;
      const letters = this.initialsOn ? this.initials : '';
      const keyInitials = this.dataset.keyInitials || 'Inicjały';
      const keyBag = this.dataset.keyBag || 'Torebka';

      /** @type {Record<string, string>} */
      const bagProperties = {};
      for (const [name, value] of formData.entries()) {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (match && match[1] && typeof value === 'string' && value !== '') bagProperties[match[1]] = value;
      }
      if (letters) bagProperties[keyInitials] = letters;

      /** @type {Array<Record<string, any>>} */
      const items = [];
      /** @type {Record<string, any>} */
      const bag = { id: variantId, quantity };
      if (Object.keys(bagProperties).length) bag.properties = bagProperties;
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) bag.selling_plan = Number(sellingPlan);
      items.push(bag);

      const initialsVariant = Number(this.dataset.iniVariant);
      if (letters && initialsVariant) {
        items.push({
          id: initialsVariant,
          quantity,
          parent_id: variantId,
          properties: { [keyInitials]: letters, [keyBag]: this.dataset.bagTitle || '' },
        });
      }
      const flapIds = this.selectedFlaps;
      for (const flapVariant of flapIds) {
        items.push({ id: flapVariant, quantity: 1, parent_id: variantId });
      }

      /** @type {{ soldOut: string[], failed: number[] }} */
      const notice = { soldOut: [], failed: [] };
      /**
       * Dodanie się nie udało, a w zamówieniu są przypinki.
       * all = false: wyprzedane przypinki wypadają z rzędu i z zamówienia (null, gdy żadna nie jest wyprzedana);
       * all = true: ostatnia próba bez przypinek, żeby torebka (i inicjały) trafiły do koszyka.
       * @param {Array<Record<string, any>>} current
       * @param {boolean} all
       */
      const recover = async (current, all) => {
        const flapsNow = current.filter((item) => flapIds.includes(Number(item.id)));
        if (!flapsNow.length) return null;
        let drop = flapsNow;
        if (all) {
          notice.failed = drop.map((item) => Number(item.id));
        } else {
          const gone = await this.recheckFlaps();
          drop = flapsNow.filter((item) => gone.has(Number(item.id)));
          if (!drop.length) return null;
          notice.soldOut = notice.soldOut.concat(drop.map((item) => gone.get(Number(item.id)) || ''));
        }
        const dropIds = new Set(drop.map((item) => Number(item.id)));
        return current.filter((item) => !(flapIds.includes(Number(item.id)) && dropIds.has(Number(item.id))));
      };

      await addToCart(items, source, this.dataset.blockId || SOURCE, source.dataset.productId || '', recover);
      announce(source, this.dataset.msgAdded || '');

      if (notice.failed.length) this.#uncheckFlaps(notice.failed);
      if (notice.soldOut.length) {
        const template =
          notice.soldOut.length > 1 ? this.dataset.msgFlapsGone || '' : this.dataset.msgFlapGone || '';
        this.#setError(fillNames(template, notice.soldOut));
      } else if (notice.failed.length) {
        this.#setError(this.dataset.msgFlapFailed || '');
      }
    } catch (error) {
      const detail = error instanceof Error && error.message ? ` ${error.message}` : '';
      this.#setError(`${this.dataset.msgError || ''}${detail}`.trim());
    } finally {
      this.busy = false;
    }
  }

  #syncPanel() {
    const { iniPanel } = this.refs;
    if (iniPanel) iniPanel.hidden = !this.initialsOn;
    this.#updateCount(cleanInitials(this.refs.iniInput?.value || '').letters);
  }

  /** @param {number} letters */
  #updateCount(letters) {
    if (this.refs.iniCount) this.refs.iniCount.textContent = `${letters}/${MAX_LETTERS}`;
  }

  /** @param {string} text */
  #setMessage(text) {
    const { iniMsg } = this.refs;
    if (!iniMsg) return;
    iniMsg.textContent = text;
    iniMsg.hidden = !text;
  }

  /** @param {string} text */
  #setError(text) {
    const { err } = this.refs;
    if (!err) return;
    err.textContent = text;
    err.hidden = !text;
  }
}

/**
 * @param {Array<Record<string, any>>} items
 * @param {string[]} sectionIds
 * @returns {Promise<{ ok: boolean, data: any }>}
 */
async function postItems(items, sectionIds) {
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });
  /** @type {any} */
  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  return { ok: response.ok && !data.status, data };
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('');
  return response.json();
}

/**
 * Jedno żądanie /cart/add.js + zdarzenia motywu (jak product-form.js).
 * @param {Array<Record<string, any>>} items
 * @param {HTMLElement} source - element, z którego idzie zdarzenie (formularz produktu)
 * @param {string} sourceId
 * @param {string} productId
 * @param {(items: Array<Record<string, any>>, all: boolean) => Promise<Array<Record<string, any>> | null>} [recover]
 *   po nieudanym dodaniu zwraca pozycje do ponownej próby (bez wyprzedanych przypinek albo, przy all, bez przypinek)
 *   lub null, gdy nie ma czego odrzucić
 */
async function addToCart(items, source, sourceId, productId, recover) {
  const sectionIds = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
  }
  const itemCount = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: Number(item.quantity) || 1 })),
      promise: deferred.promise,
    })
  );

  try {
    let current = items;
    let result = await postItems(current, sectionIds);
    if (!result.ok && recover) {
      // wyprzedana przypinka: bez niej
      const retry = await recover(current, false);
      if (retry && retry.length && retry.length < current.length) {
        current = retry;
        result = await postItems(current, sectionIds);
      }
    }
    if (!result.ok && current.some((item) => item.parent_id)) {
      // awaryjnie (jak LxR III): te same pozycje bez zagnieżdżenia pod torebką
      result = await postItems(
        current.map(({ parent_id, ...rest }) => rest),
        sectionIds
      );
    }
    if (!result.ok && recover) {
      // ostatnia próba: torebka (i inicjały) bez przypinek
      const retry = await recover(current, true);
      if (retry && retry.length && retry.length < current.length) {
        current = retry;
        result = await postItems(current, sectionIds);
      }
    }

    const cart = await fetchCart();

    if (!result.ok) {
      const data = result.data || {};
      source.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: SOURCE, sourceId, itemCount, productId },
      });
      throw new Error(typeof data.description === 'string' ? data.description : '');
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: SOURCE,
        sourceId,
        itemCount,
        productId,
        sections: result.data?.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Komunikat dla czytników ekranu w regionie formularza produktu.
 * @param {HTMLElement} source
 * @param {string} text
 */
function announce(source, text) {
  const region = source.querySelector('[ref="liveRegion"]');
  if (!(region instanceof HTMLElement) || !text) return;
  region.textContent = text;
  setTimeout(() => {
    if (region.textContent === text) region.textContent = '';
  }, 5000);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Blok dodatków dla formularza produktu (ta sama sekcja i produkt; poza oknem quick add).
 * @param {EventTarget | null | undefined} form
 * @returns {LobosDodatki | null}
 */
function addonsFor(form) {
  if (!(form instanceof HTMLFormElement)) return null;
  const productForm = form.closest('product-form-component');
  if (!(productForm instanceof HTMLElement) || productForm.closest('dialog')) return null;
  const section = productForm.closest('.shopify-section');
  if (!section) return null;
  for (const element of section.querySelectorAll('lobos-dodatki')) {
    if (element instanceof LobosDodatki && element.dataset.productId === productForm.dataset.productId) {
      return element;
    }
  }
  return null;
}

/**
 * Blok dodatków dla przycisku z paska na dole (ta sama sekcja i produkt).
 * @param {Element} button
 * @returns {LobosDodatki | null}
 */
function addonsForSticky(button) {
  const bar = button.closest('sticky-add-to-cart');
  if (!(bar instanceof HTMLElement)) return null;
  const section = bar.closest('.shopify-section');
  if (!section) return null;
  for (const element of section.querySelectorAll('lobos-dodatki')) {
    if (element instanceof LobosDodatki && element.dataset.productId === bar.dataset.productId) return element;
  }
  return null;
}

function installInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  // Klik w „Dodaj do koszyka” przy błędnych inicjałach: bez animacji i bez wysyłki.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;

      // Przycisk w pasku na dole (sticky): zatrzymujemy, zanim pasek pokaże animację „dodano”.
      const stickyButton = target?.closest('sticky-add-to-cart .sticky-add-to-cart__button');
      if (stickyButton instanceof HTMLButtonElement) {
        const addons = addonsForSticky(stickyButton);
        if (!addons || !addons.hasSelection) return;
        const check = addons.validate();
        if (check.ok && !addons.busy) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!check.ok) addons.showInvalid(check, true);
        return;
      }

      const button = target?.closest('button[name="add"]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;
      const addons = addonsFor(button.form);
      if (!addons || !addons.hasSelection) return;
      const check = addons.validate();
      if (check.ok && !addons.busy) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const fromStickyBar = button.dataset.puppet === 'true';
      if (fromStickyBar) button.dataset.puppet = 'false';
      if (!check.ok) addons.showInvalid(check, fromStickyBar);
    },
    true
  );

  // Wysyłka formularza produktu z zaznaczonymi dodatkami: jedno żądanie zamiast formularza motywu.
  window.addEventListener(
    'submit',
    (event) => {
      const addons = addonsFor(event.target);
      if (!addons || !addons.hasSelection) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addons.submit(/** @type {HTMLFormElement} */ (event.target));
    },
    true
  );
}

// Rejestracja na końcu modułu: define() od razu podłącza elementy już obecne w DOM (connectedCallback),
// więc wszystkie zmienne modułu muszą być wtedy zainicjalizowane (poprawka 5.10: TDZ interceptorsInstalled).
if (!customElements.get('lobos-dodatki')) {
  customElements.define('lobos-dodatki', LobosDodatki);
}
